import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchCoreMetrics, PlatformData } from '../services/analyticsService';
import { socialService, SocialConnection } from '../services/socialService';
import { FileText, Users, Image, TrendingUp, AlertCircle, Lock, Crown, Info } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import ConnectPlaceholder from './dashboard/ConnectPlaceholder';
import { useSocialConnections } from '../hooks/useSocialConnections';
import { useAnalyticsRetention } from '../hooks/useAnalyticsRetention';
import { useNavigate } from 'react-router-dom';
import AnalyticsLoadingCard from './ui/AnalyticsLoadingCard';

import { getPlatformColor } from '../config/platforms';

const getPlatformColors = (platforms: string[]) => {
  const colors: { [key: string]: string } = {};
  platforms.forEach(platform => {
    colors[platform] = getPlatformColor(platform);
  });
  return colors;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg">
        <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{label}</p>
        {payload.map((pld: any) => (
          <div key={pld.dataKey} className="flex items-center space-x-2 mt-1">
            <div style={{ width: 10, height: 10, backgroundColor: pld.stroke, borderRadius: '50%' }}></div>
            <p className="text-sm text-gray-700 dark:text-gray-300">{`${pld.dataKey}: ${pld.value}`}</p>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const transformHistoryData = (historyData: PlatformData[] | undefined) => {
  if (!historyData) return [];

  const transformedData: { [key: string]: any } = {};

  historyData.forEach(platform => {
    platform.data.forEach(monthData => {
      if (!transformedData[monthData.name]) {
        transformedData[monthData.name] = { name: monthData.name };
      }
      transformedData[monthData.name][platform.name] = monthData.value;
    });
  });

  return Object.values(transformedData);
};

const PlatformChart: React.FC<{
  data: PlatformData[] | undefined,
  title: string,
  connectedPlatforms?: string[]
}> = ({ data, title, connectedPlatforms }) => {
  const transformedData = transformHistoryData(data);
  const platforms = data?.map(p => p.name) || [];
  const colors = getPlatformColors(platforms);

  // Check if all platforms have the same values (overlapping issue)
  const hasOverlappingData = (data: any[]) => {
    if (data.length === 0) return false;
    const firstDataPoint = data[0];
    const allPlatformValues = platforms.map(platform => firstDataPoint[platform]).filter(val => val !== undefined);
    return allPlatformValues.length > 1 && allPlatformValues.every(val => val === allPlatformValues[0]);
  };

  const isOverlapping = hasOverlappingData(transformedData);

  // Format Y-axis values to show appropriate precision
  const formatYAxisValue = (value: number) => {
    if (title.includes('Engagement')) {
      // For engagement rates, show with 1 decimal place if small, whole numbers if large
      return value < 10 ? value.toFixed(1) : Math.round(value).toString();
    } else if (title.includes('Follower')) {
      // For follower growth, show with 1 decimal place if small, whole numbers if large
      return value < 10 ? value.toFixed(1) : Math.round(value).toString();
    }
    return value.toString();
  };

  // Format tooltip values with proper context and units
  const formatTooltipValue = (value: number) => {
    if (title.includes('Engagement')) {
      // Show engagement rate as percentage
      return `${value.toFixed(2)}% engagement rate`;
    } else if (title.includes('Follower')) {
      // Show follower growth as percentage or count
      return value < 100 ? `${value.toFixed(1)}% growth` : `${Math.round(value)} new followers`;
    }
    return value.toFixed(2);
  };

  // Check if there's no data to display
  if (!data || data.length === 0 || transformedData.length === 0) {
    return (
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">{title}</h3>
        <div className="h-64 flex items-center justify-center bg-gray-50 dark:bg-slate-800/50 rounded-lg border border-gray-200 dark:border-slate-700">
          <div className="text-center">
            <div className="text-gray-400 dark:text-gray-500 mb-2">📊</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">No data available yet</p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Start posting to see your analytics</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={transformedData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(128, 128, 128, 0.2)" />
          <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={{ stroke: 'rgba(128, 128, 128, 0.2)' }} tickLine={{ stroke: 'rgba(128, 128, 128, 0.2)' }} />
          <YAxis
            tick={{ fill: '#6b7280', fontSize: 12 }}
            axisLine={{ stroke: 'rgba(128, 128, 128, 0.2)' }}
            tickLine={{ stroke: 'rgba(128, 128, 128, 0.2)' }}
            tickFormatter={formatYAxisValue}
            domain={['dataMin', 'dataMax']}
            allowDecimals={true}
            width={60}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                return (
                  <div className="p-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg">
                    <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{label}</p>
                    {payload.map((pld: any) => (
                      <div key={pld.dataKey} className="flex items-center space-x-2 mt-1">
                        <div style={{ width: 10, height: 10, backgroundColor: pld.stroke, borderRadius: '50%' }}></div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{`${pld.dataKey}: ${formatTooltipValue(pld.value)}`}</p>
                      </div>
                    ))}
                  </div>
                );
              }
              return null;
            }}
            cursor={{ stroke: 'rgba(128, 128, 128, 0.1)', strokeWidth: 2 }}
          />
          <Legend
            iconType="circle"
            iconSize={8}
            wrapperStyle={isOverlapping ? { paddingTop: '10px', fontSize: '12px' } : {}}
          />
          {isOverlapping && (
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: 'rgba(255,255,255,0.9)',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              color: '#666'
            }}>
              ⚠️ Lines overlap (same values)
            </div>
          )}
          {platforms.map((platform, index) => (
            <Line
              key={platform}
              type="monotone"
              dataKey={platform}
              stroke={colors[platform] || '#000000'}
              strokeWidth={isOverlapping ? 3 + index : 2.5}
              strokeDasharray={isOverlapping && index > 0 ? "5,5" : "0"}
              activeDot={{ r: 7, strokeWidth: 2, fill: colors[platform] }}
              dot={{ r: isOverlapping ? 5 + index : 4, strokeWidth: 2 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      {/* Show data table when lines overlap */}
      {isOverlapping && transformedData.length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            📊 Platform Values (Lines Overlap)
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {platforms.map((platform) => (
              <div key={platform} className="flex items-center space-x-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: colors[platform] }}
                ></div>
                <span className="text-gray-600 dark:text-gray-400">
                  {platform}: {transformedData[0]?.[platform]?.toFixed(2) || '0.00'}
                  {title.includes('Growth') ? '%' : title.includes('Engagement') ? '%' : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CoreMetricsCard: React.FC = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = React.useState<'7d' | '1m' | '3m'>('1m');
  const { retentionDays, isUnlimited, tierName } = useAnalyticsRetention();
  
  // Check which date ranges are available based on retention
  const isDateRangeAvailable = (range: '7d' | '1m' | '3m') => {
    if (isUnlimited) return true;
    const daysMap = { '7d': 7, '1m': 30, '3m': 90 };
    return daysMap[range] <= retentionDays;
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['coreMetrics', dateRange, 'v2'], // Add version to force refresh
    queryFn: () => {
      // console.log(`📊 CoreMetricsCard: Fetching data for ${dateRange}`);
      return fetchCoreMetrics(dateRange);
    },
    staleTime: 0, // Force refresh when date range changes
    cacheTime: 0, // Don't cache at all
    retry: 1, // Only retry once to avoid infinite loading
  });

  const { connections, isLoading: connectionsLoading, error: connectionsError, handleConnect } = useSocialConnections();

  if (isLoading || connectionsLoading) {
    return (
      <AnalyticsLoadingCard
        title="Core Social Metrics"
        message="Loading your engagement rates, follower growth, and reach data..."
        icon="TrendingUp"
      />
    );
  }
  if (error) {
    return (
      <div className="bg-white dark:bg-slate-900/70 rounded-2xl p-8 border border-gray-200/80 dark:border-slate-800 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Core Social Metrics</h2>
          <button
            onClick={() => refetch()}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
        <div className="text-red-600 dark:text-red-400">
          Error loading core metrics: {error.message}
        </div>
      </div>
    );
  }
  if (connectionsError) return <div>Error loading connections: {connectionsError.message}</div>;

  if (!connections || connections.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-900/70 rounded-2xl p-8 border border-gray-200/80 dark:border-slate-800 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Core Social Metrics</h2>
            {connections && connections.length > 0 && (
              <div className="flex items-center mt-2 space-x-2">
                <div className="flex items-center space-x-1">
                  {connections.slice(0, 3).map((connection, index) => (
                    <div
                      key={connection.provider}
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: getPlatformColor(connection.provider) }}
                    />
                  ))}
                  {connections.length > 3 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">+{connections.length - 3}</span>
                  )}
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Combined from {connections.length} platform{connections.length > 1 ? 's' : ''} • Last {dateRange === '7d' ? '7 days' : dateRange === '1m' ? '30 days' : '90 days'}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {!isUnlimited && (
              <div className="px-2 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                {retentionDays} days retention
              </div>
            )}
            <div className="flex items-center space-x-2 bg-gray-100 dark:bg-slate-800/50 rounded-lg p-1">
              {(['7d', '1m', '3m'] as const).map((range) => {
                const available = isDateRangeAvailable(range);
                return (
                  <button
                    key={range}
                    onClick={() => available ? setDateRange(range) : navigate('/pricing')}
                    disabled={!available}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center space-x-1 ${
                      dateRange === range
                        ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 shadow-sm'
                        : available
                          ? 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                          : 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60'
                    }`}
                    title={!available ? `Upgrade to access ${range.toUpperCase()} data` : ''}
                  >
                    <span>{range.toUpperCase()}</span>
                    {!available && <Lock className="w-3 h-3" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <ConnectPlaceholder
          message="Connect your social media accounts to see your core metrics."
          onConnectClick={() => handleConnect('facebook')}
        />
      </div>
    );
  }

  const metrics = [
    { name: 'Engagement Rate', value: data?.engagementRate, icon: TrendingUp, color: 'text-indigo-600 dark:text-indigo-500' },
    { name: 'Follower Growth', value: data?.followerGrowth, icon: Users, color: 'text-emerald-600 dark:text-emerald-500' },
    { name: 'Reach', value: data?.reach, icon: FileText, color: 'text-sky-600 dark:text-sky-500' },
    { name: 'Impressions', value: data?.impressions, icon: Image, color: 'text-amber-600 dark:text-amber-500' },
    { name: 'CTR', value: data?.ctr, icon: TrendingUp, color: 'text-rose-600 dark:text-rose-500' },
  ];

  const connectedPlatforms = connections?.map(c => c.provider.toLowerCase()) || [];
  console.log('📊 Connected platforms:', connectedPlatforms);
  console.log('📊 Platform engagement history:', data?.platformEngagementHistory?.map(p => p.name));
  console.log('📊 Platform follower growth history:', data?.platformFollowerGrowthHistory?.map(p => p.name));
  console.log('📊 Raw engagement data:', data?.platformEngagementHistory);
  console.log('📊 Raw follower growth data:', data?.platformFollowerGrowthHistory);

  const filteredEngagementHistory = data?.platformEngagementHistory.filter(p => {
    const platformName = p.name.toLowerCase();
    const included = connectedPlatforms.includes(platformName);
    console.log(`📊 Engagement - Platform ${p.name} (${platformName}) included: ${included}`);
    console.log(`📊 Connected platforms for comparison:`, connectedPlatforms);
    return included;
  });
  const filteredFollowerGrowthHistory = data?.platformFollowerGrowthHistory.filter(p => {
    const platformName = p.name.toLowerCase();
    const included = connectedPlatforms.includes(platformName);
    console.log(`📊 Follower Growth - Platform ${p.name} (${platformName}) included: ${included}`);
    console.log(`📊 Connected platforms for comparison:`, connectedPlatforms);
    return included;
  });

  return (
    <div className="bg-white dark:bg-slate-900/70 rounded-2xl p-8 border border-gray-200/80 dark:border-slate-800 shadow-sm">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Core Social Metrics</h2>
            <div className="relative group">
              <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                <Info className="w-5 h-5" />
              </button>
              <div className="absolute left-0 top-8 w-80 p-4 bg-gray-900 dark:bg-gray-800 text-white rounded-lg shadow-xl border border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <p className="font-semibold mb-2">Understanding Core Metrics:</p>
                <ul className="space-y-1.5 text-xs text-gray-200">
                  <li>• <strong className="text-white">Aggregated Data:</strong> Combines all connected platforms</li>
                  <li>• <strong className="text-white">Engagement Rate:</strong> Total interactions ÷ (followers × posts)</li>
                  <li>• <strong className="text-white">Growth:</strong> Requires 2+ days of data to calculate</li>
                  <li>• <strong className="text-white">Reach/Impressions:</strong> Facebook requires 100+ followers for insights</li>
                  <li>• <strong className="text-white">Retention:</strong> Historical data limited by your tier</li>
                  <li>• <strong className="text-white">Updates:</strong> Metrics collected daily at 2 AM UTC</li>
                </ul>
                <div className="absolute -top-2 left-4 w-4 h-4 bg-gray-900 dark:bg-gray-800 border-l border-t border-gray-700 transform rotate-45"></div>
              </div>
            </div>

          </div>
          {connections && connections.length > 0 && (
            <div className="flex items-center mt-2 space-x-2">
              <div className="flex items-center space-x-1">
                {connections.slice(0, 3).map((connection, index) => (
                  <div
                    key={connection.provider}
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: getPlatformColor(connection.provider) }}
                  />
                ))}
                {connections.length > 3 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">+{connections.length - 3}</span>
                )}
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Combined from {connections.length} platform{connections.length > 1 ? 's' : ''} • Last {dateRange === '7d' ? '7 days' : dateRange === '1m' ? '30 days' : '90 days'}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {!isUnlimited && (
            <div className="px-2 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
              {retentionDays} days retention
            </div>
          )}
          <div className="flex items-center space-x-2 bg-gray-100 dark:bg-slate-800/50 rounded-lg p-1">
            {(['7d', '1m', '3m'] as const).map((range) => {
              const available = isDateRangeAvailable(range);
              return (
                <button
                  key={range}
                  onClick={() => available ? setDateRange(range) : navigate('/pricing')}
                  disabled={!available}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center space-x-1 ${
                    dateRange === range
                      ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 shadow-sm'
                      : available
                        ? 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        : 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60'
                  }`}
                  title={!available ? `Upgrade to access ${range.toUpperCase()} data` : ''}
                >
                  <span>{range.toUpperCase()}</span>
                  {!available && <Lock className="w-3 h-3" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {metrics.map((metric) => (
          <div key={metric.name} className="flex items-center space-x-4 bg-gray-100 dark:bg-slate-800/50 p-4 rounded-lg">
            <div className="p-3 rounded-full bg-gray-200 dark:bg-slate-900/80">
              <metric.icon className={`h-6 w-6 ${metric.color}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{metric.name}</p>
              <p className="text-2xl font-bold !text-black dark:!text-white">{metric.value}</p>
            </div>
          </div>
        ))}
      </div>

      <PlatformChart
        data={filteredEngagementHistory}
        title="Platform Engagement History"
        connectedPlatforms={connectedPlatforms}
      />
      <PlatformChart
        data={filteredFollowerGrowthHistory}
        title="Platform Follower Growth History"
        connectedPlatforms={connectedPlatforms}
      />

      {/* Show data availability warning */}
      {data?.dataAvailability?.message && (
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center space-x-2 mb-2">
            <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
              Limited Historical Data
            </h4>
          </div>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {data.dataAvailability.message}
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
            📊 More historical data will be available as your account activity increases over time.
          </p>
        </div>
      )}

      {/* Show missing platforms info */}
      {data?.missingPlatforms && data.missingPlatforms.length > 0 && (
        <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <div className="flex items-center space-x-2 mb-2">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <h4 className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Some Platform Data Unavailable
            </h4>
          </div>
          <div className="space-y-1">
            {data.missingPlatforms.map((platform) => (
              <div key={platform.name} className="flex items-center justify-between text-xs">
                <span className="text-amber-700 dark:text-amber-300 font-medium">
                  {platform.name}
                </span>
                <span className="text-amber-600 dark:text-amber-400">
                  {platform.reason}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
            💡 Data will be available once API limits reset or connectivity is restored.
          </p>
        </div>
      )}

    </div>
  );
};


export default CoreMetricsCard;